import type { DirectionsResult, DraftRoute, RouteOptions, Waypoint } from '@cad/core';
import { expectedRequestCount, formatDistance, formatDuration } from '@cad/core';
import { HelpPanel } from './HelpPanel';
import { ShareBar } from './ShareBar';
import { WaypointList } from './WaypointList';

export interface SidebarProps {
  username: string;
  onNewRoute: () => void;
  onOpenRoutes: () => void;
  onSignOut: () => void;
  draft: DraftRoute;
  route: DirectionsResult | null;
  routing: boolean;
  routingError: string | null;
  selectedId: string | null;
  dirty: boolean;
  savedId: string | null;
  saving: boolean;
  saveError: string | null;
  canUndo: boolean;
  canRedo: boolean;
  onNameChange: (name: string) => void;
  onNotesChange: (notes: string) => void;
  onOptionChange: (options: Partial<RouteOptions>) => void;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onToggleKind: (id: string, kind: Waypoint['kind']) => void;
  onRename: (id: string, name: string) => void;
  onReorder: (from: number, to: number) => void;
  onReverse: () => void;
  onClear: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onFit: () => void;
}

export function Sidebar(props: SidebarProps) {
  const { draft, route } = props;
  const stops = draft.waypoints.filter((w) => w.kind === 'stop').length;
  const requests = expectedRequestCount(draft.waypoints.length);

  return (
    <aside className="sidebar">
      <div className="account-bar">
        <button type="button" className="account-routes" onClick={props.onNewRoute}>
          New
        </button>
        <button type="button" className="account-routes" onClick={props.onOpenRoutes}>
          Saved routes
        </button>
        <span className="account-who" title={props.username}>
          {props.username}
        </span>
        <button type="button" className="account-out" onClick={props.onSignOut}>
          Sign out
        </button>
      </div>

      <header className="sidebar-header">
        <input
          className="route-name"
          value={draft.name}
          onChange={(event) => props.onNameChange(event.target.value)}
          placeholder="Name this route"
          aria-label="Route name"
        />
        <div className="history-buttons">
          <button type="button" onClick={props.onUndo} disabled={!props.canUndo} title="Undo (⌘Z)">
            ↺
          </button>
          <button type="button" onClick={props.onRedo} disabled={!props.canRedo} title="Redo (⇧⌘Z)">
            ↻
          </button>
        </div>
      </header>

      <section className="stats">
        <div>
          <div className="stat-value">{route ? formatDistance(route.distance) : '—'}</div>
          <div className="stat-label">Distance</div>
        </div>
        <div>
          <div className="stat-value">{route ? formatDuration(route.duration) : '—'}</div>
          <div className="stat-label">Driving</div>
        </div>
        <div>
          <div className="stat-value">{stops}</div>
          <div className="stat-label">Stops</div>
        </div>
      </section>

      {props.routing && <div className="notice">Routing…</div>}
      {props.routingError && <div className="notice error">{props.routingError}</div>}
      {requests > 1 && !props.routing && (
        <div className="notice subtle">
          {draft.waypoints.length} points, routed in {requests} requests and stitched together.
        </div>
      )}

      <section className="options">
        <label>
          <input
            type="checkbox"
            checked={draft.options.avoidMotorways}
            onChange={(event) => props.onOptionChange({ avoidMotorways: event.target.checked })}
          />
          Avoid motorways
        </label>
        <label>
          <input
            type="checkbox"
            checked={draft.options.avoidTolls}
            onChange={(event) => props.onOptionChange({ avoidTolls: event.target.checked })}
          />
          Avoid tolls
        </label>
        <label>
          <input
            type="checkbox"
            checked={draft.options.avoidFerries}
            onChange={(event) => props.onOptionChange({ avoidFerries: event.target.checked })}
          />
          Avoid ferries
        </label>
      </section>

      <div className="list-scroll">
        <WaypointList
          waypoints={draft.waypoints}
          selectedId={props.selectedId}
          onSelect={props.onSelect}
          onRemove={props.onRemove}
          onToggleKind={props.onToggleKind}
          onRename={props.onRename}
          onReorder={props.onReorder}
        />
      </div>

      <textarea
        className="notes"
        value={draft.notes ?? ''}
        onChange={(event) => props.onNotesChange(event.target.value)}
        placeholder="Notes for the group — meeting time, fuel stops, where lunch is."
        rows={3}
      />

      <div className="toolbar">
        <button type="button" onClick={props.onReverse} disabled={draft.waypoints.length < 2}>
          Reverse
        </button>
        <button type="button" onClick={props.onFit} disabled={!route}>
          Fit
        </button>
        <button type="button" onClick={props.onClear} disabled={draft.waypoints.length === 0}>
          Clear
        </button>
      </div>

      <HelpPanel />

      {props.saveError && <div className="notice error">{props.saveError}</div>}

      <button
        type="button"
        className="save-button"
        onClick={props.onSave}
        disabled={!route || props.saving || (!props.dirty && props.savedId !== null)}
      >
        {props.saving
          ? 'Saving…'
          : props.savedId && !props.dirty
            ? 'Saved'
            : props.savedId
              ? 'Save changes'
              : 'Save & get share link'}
      </button>

      {props.savedId && <ShareBar routeId={props.savedId} dirty={props.dirty} />}
    </aside>
  );
}
